import { AiFillFileZip } from 'react-icons/ai';
import {
  BsFiletypeCsv,
  BsFiletypePpt,
  BsFiletypePptx,
  BsFiletypeTxt,
  BsFiletypeXls,
  BsFiletypeXlsx,
} from 'react-icons/bs';
import { ImFilePdf } from 'react-icons/im';
import {
  IoDocumentText,
  IoImage,
  IoPlayCircle,
} from 'react-icons/io5';

import {
  FileType,
  SingleFileType,
} from '../types/communityMessagesTypes';
import {
  getFileExtension,
  showLoadingToastWithProgress,
} from './utils';

export const ACCEPTED_FILE_TYPES_EXPANDED = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/svg',
  'image/gif',
  'image/webp',
  'image/tiff',
  'image/heic',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/mov',
  'video/mkv',
  'audio/mp3',
  'audio/mpeg',
  'audio/m4a',
  'audio/aac',
  'audio/m3u',
  'audio/oga',
  'audio/wav',
  'application/pdf',
] as const;

export type AcceptedFileTypeExpanded =
  (typeof ACCEPTED_FILE_TYPES_EXPANDED)[number];

export const ACCEPTED_IMAGE_FILE_TYPES = [
  'jpg',
  'jpeg',
  'png',
  'svg',
  'gif',
  'webp',
  'tiff',
  'heic',
] as const;

export type AcceptedImageFileType = (typeof ACCEPTED_IMAGE_FILE_TYPES)[number];

export const ACCEPTED_VIDEO_FILE_TYPES = [
  'mp4',
  'webm',
  'quicktime',
  'mov',
  'mkv',
] as const;

export type AcceptedVideoFileType = (typeof ACCEPTED_VIDEO_FILE_TYPES)[number];

export const ACCEPTED_AUDIO_FILE_TYPES = [
  'mp3',
  'mpeg',
  'm4a',
  'aac',
  'm3u',
  'oga',
  'wav',
] as const;

export type AcceptedAudioFileType = (typeof ACCEPTED_AUDIO_FILE_TYPES)[number];

export const ACCEPTED_DOC_FILE_TYPES = [
  ...ACCEPTED_IMAGE_FILE_TYPES,
  ...ACCEPTED_VIDEO_FILE_TYPES,
  ...ACCEPTED_AUDIO_FILE_TYPES,
  'pdf',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
  'csv',
  'txt',
  'rtf',
  'html',
  'xml',
  'json',
  'zip',
  'rar',
] as const;

export type AcceptedDocFileType = (typeof ACCEPTED_DOC_FILE_TYPES)[number];

export const SPECIFIC_FILE_ICONS: Partial<
  Record<AcceptedDocFileType, React.ReactNode>
> = {
  mp4: <IoPlayCircle />,
  mov: <IoPlayCircle />,
  jpg: <IoImage />,
  jpeg: <IoImage />,
  png: <IoImage />,
  svg: <IoImage />,
  gif: <IoImage />,
  webp: <IoImage />,
  pdf: <ImFilePdf />,
  doc: <IoDocumentText />,
  docx: <IoDocumentText />,
  ppt: <BsFiletypePpt />,
  pptx: <BsFiletypePptx />,
  xls: <BsFiletypeXls />,
  xlsx: <BsFiletypeXlsx />,
  csv: <BsFiletypeCsv />,
  txt: <BsFiletypeTxt />,
  rtf: <IoDocumentText />,
  html: <IoDocumentText />,
  xml: <IoDocumentText />,
  json: <IoDocumentText />,
  zip: <AiFillFileZip />,
  rar: <IoDocumentText />,
};

export const getSpecificFileIcon = (
  fileType: AcceptedDocFileType,
): React.ReactNode | undefined => {
  return SPECIFIC_FILE_ICONS[fileType];
};

export const downloadFile = async (url: string, fileName?: string) => {
  // const hideLoading = message.loading('Downloading...');

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    // Convert the response to a Blob
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = blobUrl;
    link.download = fileName || url;
    link.style.display = 'none';

    document.body.appendChild(link);

    link.click();

    // Cleanup: Remove link immediately after clicking
    // This is important to avoid memory leaks
    // and to ensure the link is not visible in the DOM
    document.body.removeChild(link);

    // Delay URL revocation to prevent Safari and large file issues
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000); // 1s delay (adjust as needed)

    // hideLoading();
  } catch (error) {
    console.log('Error downloading file:', error);
  }
};

export const downloadFileWithProgress = async (
  url: string,
  fileName?: string,
) => {
  const loading = showLoadingToastWithProgress('Downloading...');

  try {
    const response = await fetch(url);

    if (!response.ok || !response.body) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    const contentLength = response.headers.get('Content-Length');
    const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;

    const disposition = response.headers.get('Content-Disposition');
    const extractedFileName = disposition
      ?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]
      ?.replace(/['"]/g, '');

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let receivedBytes = 0;

    const updateProgress = () => {
      if (totalBytes) {
        const percent = Math.min(
          100,
          Math.round((receivedBytes / totalBytes) * 100),
        );
        loading.update(percent);
      }
    };

    let done = false;

    while (!done) {
      // eslint-disable-next-line no-await-in-loop
      const { done: isDone, value } = await reader.read();

      done = isDone;

      if (value) {
        chunks.push(value);
        receivedBytes += value.length;
        updateProgress();
      }
    }

    // Convert the array of Uint8Arrays into a single Uint8Array, then pass its underlying buffer to Blob
    const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
    const mergedArray = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      mergedArray.set(chunk, offset);
      offset += chunk.length;
    }

    const blob = new Blob([mergedArray.buffer]);
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = blobUrl;
    link.download = fileName || extractedFileName || 'download';
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

    loading.success('Download complete!');
  } catch (error) {
    console.error('Error downloading file:', error);
    loading.error('Failed to download file.');
  }
};

export const getGenericFileType = (
  fileName: string,
  file?: File,
): AcceptedDocFileType | undefined => {
  const fileExtension = getFileExtension(fileName) || undefined;

  if (!fileExtension) {
    return undefined;
  }

  if (
    ACCEPTED_IMAGE_FILE_TYPES.includes(
      fileExtension as unknown as AcceptedImageFileType,
    )
  ) {
    return FileType.IMAGE as AcceptedDocFileType;
  }

  if (
    ACCEPTED_VIDEO_FILE_TYPES.includes(
      fileExtension as unknown as AcceptedVideoFileType,
    )
  ) {
    return FileType.VIDEO as AcceptedDocFileType;
  }

  if (
    ACCEPTED_AUDIO_FILE_TYPES.includes(
      fileExtension as unknown as AcceptedAudioFileType,
    )
  ) {
    return FileType.AUDIO as AcceptedDocFileType;
  }

  if (
    ACCEPTED_DOC_FILE_TYPES.includes(
      fileExtension as unknown as AcceptedDocFileType,
    )
  ) {
    return (file?.type?.split?.('/')?.[0] ||
      FileType.DOC) as SingleFileType as AcceptedDocFileType;
  }

  return undefined;
};

export const DOC_FILE_TYPES = ['application', 'text'];
